import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Input, InputRef } from 'antd';
import { InputProps as AntdInputProps } from 'antd/lib/input';
import Icon from '../Icon';
import Spin from '../Spin';
import theme from '../theme';
import { Text } from '../Typography';
import { List } from '../List';

export interface SearchBarProps
  extends Omit<AntdInputProps, 'onChange' | 'allowClear'> {
  label?: React.ReactNode;
  required?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  onChange: (value: string) => void;
  shape?: 'round';

  // #region Search Results

  resultData?: any[];
  displayResults?: boolean;
  customResultRenderer?: (
    item: any,
    index: number,
    activeIndex?: number,
  ) => React.ReactNode;
  footer?: React.ReactNode;
  resultsPosition?: 'block' | 'absolute';

  // #endregion Search Results
  dataCy?: string;
  labelWidth?: number;
}

const StyledSuffixWrapper = styled.span`
  display: flex;
  align-items: center;
`;

const StyledInput = styled(Input)<{ shape?: 'round' }>`
  ${({ shape }) => shape === 'round' && 'border-radius: 105px;'}
`;

const StyledRequired = styled(Text)`
  color: ${theme.getColor('primary')};
`;

const StyledClearButton = styled.button<{ $hide: boolean }>`
  line-height: 0;
  margin-right: 5px;
  ${({ $hide }) => $hide && `visibility: hidden`};
`;

export const SearchBar = React.forwardRef<InputRef, SearchBarProps>(
  (
    {
      value,
      onChange,
      loading,
      suffix,
      allowClear,
      label,
      displayResults,
      resultData,
      customResultRenderer,
      required,
      footer = () => <></>,
      resultsPosition = 'block',
      prefix,
      dataCy,
      labelWidth,
      ...props
    },
    ref,
  ) => {
    const [isActive, setIsActive] = useState(false);
    const [searchBarWidth, setSearchBarWidth] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const labelRef = useRef(null);

    useEffect(() => {
      setSearchBarWidth(labelRef.current.getBoundingClientRect().width);
    }, [isActive]);

    const clear = () => {
      onChange('');
    };

    const renderClearButton = () => (
        <StyledClearButton onClick={clear} $hide={!value}>
          <Icon type="x" />
        </StyledClearButton>
      );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
      if (typeof e === 'string') onChange(e);
      else onChange(e.target.value);
    };

    const valueExists = (_value: SearchBarProps['value']): boolean => {
      if (['string', 'number'].includes(typeof _value))
        return `${_value}`.length > 0;
      if (Array.isArray(_value)) return _value.some(v => valueExists(v));
      return false;
    };

    return (
      <>
        <label ref={labelRef}>
          {label && (
            <Text strong>
              {label} {required && <StyledRequired>*</StyledRequired>}
            </Text>
          )}
          <Input
            {...props}
            ref={ref}
            value={value}
            onFocus={() => setIsActive(true)}
            onBlur={() => setIsActive(false)}
            onChange={handleChange}
            prefix={
              <PrefixContainer>
                {loading ? (
                  <Spin size="small" />
                ) : (
                  prefix || <Icon type="search" width={19} />
                )}
              </PrefixContainer>
            }
            suffix={
              <StyledSuffixWrapper>
                {allowClear && renderClearButton()}
                {suffix}
              </StyledSuffixWrapper>
            }
            data-cy={dataCy ?? 'search-bar'}
          />
        </label>
        {displayResults && isActive ? (
          <ResultListContainer
            position={resultsPosition}
            width={searchBarWidth}
            onMouseDown={ev => ev.preventDefault()}
            data-cy="search-results-container"
          >
            <ResultList
              dataSource={resultData}
              renderItem={(item: any, index: number) =>
                customResultRenderer(item, index, activeIndex)
              }
            />
            {footer}
          </ResultListContainer>
        ) : null}
      </>
    );
  },
);

export default styled(SearchBar)`
  ${({ labelWidth }) => (labelWidth ? `width: ${labelWidth}px;` : '')}
`;

const PrefixContainer = styled.div`
  height: 23px;
  align-items: center;
  justify-content: center;
  display: flex;
`;

const ResultListContainer = styled.div<{
  position: 'block' | 'absolute';
  width?: number;
}>`
  max-height: 250px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  background-color: white;
  overflow-y: auto;
  z-index: 99999;
  ${({ position }) => `position: ${position};`}
  ${({ width }) => (width ? `width: ${width}px;` : '')}
`;

const ResultList = styled(List)`
  padding: 12px 0px;
  border-radius: 4px;
`;
